import { useSyncExternalStore } from "react";

// Subscribe function to listen to window resize events
const subscribe = (callback: () => void) => {
  window.addEventListener("resize", callback);
  return () => window.removeEventListener("resize", callback);
};

// Get the snapshot of the current window width
const getSnapshot = () => window.innerWidth;

// Server-side rendering (SSR) safe fallback
const getServerSnapshot = () => 1024; // Default width for SSR environments

export function useWindowWidth(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

import { useEffect, useState } from "react";

export const useCountdown = (target: Date | null) => {
  const [timeLeft, setTimeLeft] = useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  useEffect(() => {
    if (!target) return;

    const tick = () => {
      const now = new Date().getTime();
      const diff = target.getTime() - now;

      if (diff <= 0) {
        setTimeLeft({
          days: "00",
          hours: "00",
          minutes: "00",
          seconds: "00",
        });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeLeft({
        days: String(days).padStart(2, "0"),
        hours: String(hours).padStart(2, "0"),
        minutes: String(minutes).padStart(2, "0"),
        seconds: String(seconds).padStart(2, "0"),
      });
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [target]);

  return timeLeft;
};

export function useCounter(
  seconds: number,
  { startCb, endCb, startImmediately = true }: { startCb?: () => void; endCb?: () => void; startImmediately?: boolean },
) {
  const [isRunning, setIsRunning] = useState(startImmediately);
  const [count, setCount] = useState(seconds);
  const restart = () => {
    setIsRunning(true);
    setCount(seconds);
  };
  useEffect(() => {
    if (isRunning === false) {
      return;
    }
    startCb?.();
    const intervalId = setInterval(() => {
      setCount((prev) => prev - 1);
    }, 1000);

    const timeoutId = setTimeout(() => {
      setCount(0);
      setIsRunning(false);
      endCb?.();
      clearInterval(intervalId);
    }, seconds * 1000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning]);

  return [count, restart] as const;
}
